import React, { useState, useRef } from 'react';
import { uploadAPI } from '../lib/api';
import { Button } from './ui/button';
import { Camera, Upload, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PhotoUpload = ({ 
  currentPhotoUrl, 
  entityType, 
  entityId, 
  onUploadSuccess, 
  size = 'md',
  className = '' 
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
    xl: 'w-40 h-40'
  };

  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const response = await uploadAPI.uploadPhoto(file, entityType, entityId);
      toast.success('Photo uploaded successfully');
      if (onUploadSuccess) {
        onUploadSuccess(response.data.photo_url);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const clearPreview = () => {
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const displayUrl = previewUrl || getPhotoUrl(currentPhotoUrl);

  return (
    <div className={`relative group ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
        data-testid={`photo-upload-${entityType}-${entityId}`}
      />

      <div 
        className={`${sizeClasses[size]} rounded-full overflow-hidden bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-primary transition-colors`}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        {uploading ? (
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        ) : displayUrl ? (
          <img 
            src={displayUrl} 
            alt="Profile" 
            className="w-full h-full object-cover"
          />
        ) : (
          <Camera className="w-6 h-6 text-slate-400" />
        )}
      </div>

      {/* Upload overlay on hover */}
      {!uploading && (
        <div 
          className={`absolute inset-0 ${sizeClasses[size]} rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer`}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-6 h-6 text-white" />
        </div>
      )}

      {/* Clear preview button */}
      {previewUrl && !uploading && (
        <button
          onClick={(e) => { e.stopPropagation(); clearPreview(); }}
          className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export const PhotoAvatar = ({ photoUrl, name, size = 'md', className = '' }) => {
  const sizeClasses = {
    xs: 'w-8 h-8 text-xs',
    sm: 'w-10 h-10 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-lg',
    xl: 'w-24 h-24 text-2xl'
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const photoSrc = getPhotoUrl(photoUrl);

  return (
    <div className={`${sizeClasses[size]} rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 ${className}`}>
      {photoSrc ? (
        <img 
          src={photoSrc} 
          alt={name || 'Avatar'} 
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
          {getInitials(name)}
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
